/**
 * Billing rounding for the timesheet (Toggl-style).
 *
 * Rounds EACH time entry's duration to a billing increment before applying the
 * hourly rate, so the invoice matches how agencies actually bill (e.g. "round
 * up to the next 15 minutes"). Rounding is per-entry — NOT per aggregated
 * bucket — so two 10-minute blocks rounded up to 15' bill 30', matching Toggl.
 *
 * Like hourly rates ({@link ./rates}), the config is a device-local reporting
 * concern persisted in localStorage: it never mutates the stored entries and
 * needs no sync-schema migration. The raw durations stay intact; toggling the
 * mode back to "none" restores exact billing.
 */

export const ROUNDING_MODES = ["none", "up", "nearest", "down"] as const;
export type RoundingMode = (typeof ROUNDING_MODES)[number];

/** Billing increments offered in the UI, in minutes. */
export const ROUNDING_INCREMENTS_MIN = [5, 6, 10, 15, 30, 60] as const;

export interface RoundingConfig {
  mode: RoundingMode;
  /** Increment in minutes. Ignored when mode is "none". */
  minutes: number;
}

export const DEFAULT_ROUNDING: RoundingConfig = { mode: "none", minutes: 15 };

const ROUNDING_KEY = "billing_rounding";
const MS_IN_MIN = 60_000;
/** Sanity cap: nobody bills in increments above 4h. */
const MAX_INCREMENT_MIN = 240;

const isMode = (v: unknown): v is RoundingMode =>
  typeof v === "string" && (ROUNDING_MODES as readonly string[]).includes(v);

/** Valid increment: finite, > 0, ≤ 4h. Fractions allowed (tests use 0.05'). */
const isIncrement = (v: unknown): v is number =>
  typeof v === "number" && Number.isFinite(v) && v > 0 && v <= MAX_INCREMENT_MIN;

/**
 * Rounds a duration (ms) to the configured billing increment.
 * Non-positive durations collapse to 0; mode "none" (or an invalid increment)
 * returns the duration untouched.
 */
export function roundDurationMs(ms: number, config: RoundingConfig): number {
  if (!(ms > 0)) {
    return 0;
  }
  if (config.mode === "none" || !isIncrement(config.minutes)) {
    return ms;
  }
  const inc = config.minutes * MS_IN_MIN;
  const units = ms / inc;
  if (config.mode === "up") {
    return Math.ceil(units) * inc;
  }
  if (config.mode === "down") {
    return Math.floor(units) * inc;
  }
  return Math.round(units) * inc;
}

/** Reads the persisted rounding config; corrupt/missing payloads → default. */
export function getRoundingConfig(): RoundingConfig {
  if (typeof localStorage === "undefined") {
    return DEFAULT_ROUNDING;
  }
  try {
    const raw = localStorage.getItem(ROUNDING_KEY);
    if (!raw) {
      return DEFAULT_ROUNDING;
    }
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === "object") {
      const record: Record<string, unknown> = { ...parsed };
      const mode = isMode(record.mode) ? record.mode : DEFAULT_ROUNDING.mode;
      const minutes = isIncrement(record.minutes) ? record.minutes : DEFAULT_ROUNDING.minutes;
      return { mode, minutes };
    }
  } catch {
    /* corrupt payload → default */
  }
  return DEFAULT_ROUNDING;
}

/** Persists the rounding config (device-local). */
export function setRoundingConfig(config: RoundingConfig): void {
  if (typeof localStorage === "undefined") {
    return;
  }
  localStorage.setItem(ROUNDING_KEY, JSON.stringify(config));
}
